/* ==========================================================================
   Article audio clips
   Turns any <audio> inside an article body into a branded player.

   Progressive enhancement on purpose: the article HTML ships a normal
   <audio controls preload="none">, which works on its own. This script only
   takes over once it has actually run, so a reader whose JS fails, or a copy
   of the article rendered anywhere this asset isn't loaded, still gets a
   working player rather than nothing.

   Reads two optional attributes off the <audio>:
     data-title     label shown in the player
     data-duration  seconds, so the running time can be shown before any
                    network request happens (preload="none" means duration is
                    NaN until the reader presses play). Replaced by the real
                    value once metadata arrives.
   ========================================================================== */
(function () {
  'use strict';

  var ICON_PLAY = '<svg class="saif-clip__play-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4.5 2.4v11.2a.6.6 0 0 0 .93.5l8.4-5.6a.6.6 0 0 0 0-1L5.43 1.9a.6.6 0 0 0-.93.5Z"/></svg>';
  var ICON_PAUSE = '<svg class="saif-clip__pause-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.4 2h3.1v12H3.4zM9.5 2h3.1v12H9.5z"/></svg>';

  /* Elapsed time floors, the way every player does it. The TOTAL rounds, so a
     24.88s clip reads 0:25 rather than 0:24. That matches the stated
     data-duration, so the readout doesn't visibly jump the moment real
     metadata arrives. */
  function fmt(sec, round) {
    if (!isFinite(sec) || sec < 0) return '--:--';
    var whole = round ? Math.round(sec) : Math.floor(sec);
    var m = Math.floor(whole / 60);
    var s = whole % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function titleFor(audio) {
    var t = audio.getAttribute('data-title');
    if (t) return t;
    // Fall back to the file name, formatted the way the studio's picker does.
    var base = (audio.getAttribute('src') || '').split('/').pop().split('?')[0];
    var d = base.match(/^drill-([a-z]+)-mod(\d+)/i);
    if (d) return d[1].charAt(0).toUpperCase() + d[1].slice(1) + ' · Module ' + d[2];
    return base.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ') || 'Audio clip';
  }

  function upgrade(audio) {
    if (audio.dataset.saifClip === 'on') return;
    audio.dataset.saifClip = 'on';

    var title = titleFor(audio);
    var stated = parseFloat(audio.getAttribute('data-duration'));

    var wrap = document.createElement('div');
    wrap.className = 'saif-clip';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'saif-clip__play';
    btn.innerHTML = ICON_PLAY + ICON_PAUSE;
    btn.setAttribute('aria-label', 'Play ' + title);

    var main = document.createElement('div');
    main.className = 'saif-clip__main';

    var top = document.createElement('div');
    top.className = 'saif-clip__top';

    var label = document.createElement('span');
    label.className = 'saif-clip__title';
    label.textContent = title;

    var time = document.createElement('span');
    time.className = 'saif-clip__time';
    time.textContent = '0:00 / ' + fmt(stated, true);

    var seek = document.createElement('input');
    seek.type = 'range';
    seek.className = 'saif-clip__seek';
    seek.min = '0';
    seek.max = '1000';
    seek.value = '0';
    seek.step = '1';
    seek.style.setProperty('--pct', '0%');
    seek.setAttribute('aria-label', 'Seek within ' + title);

    top.appendChild(label);
    top.appendChild(time);
    main.appendChild(top);
    main.appendChild(seek);
    wrap.appendChild(btn);
    wrap.appendChild(main);

    // Swap the native control for ours, keeping the element as the source.
    audio.parentNode.insertBefore(wrap, audio);
    audio.removeAttribute('controls');
    audio.classList.add('saif-clip__audio');
    wrap.appendChild(audio);

    var scrubbing = false;

    function duration() {
      return isFinite(audio.duration) && audio.duration > 0 ? audio.duration : stated;
    }
    function paint() {
      var d = duration();
      var pct = (isFinite(d) && d > 0) ? (audio.currentTime / d) * 100 : 0;
      if (!scrubbing) {
        seek.value = String(Math.round(pct * 10));
        seek.style.setProperty('--pct', pct.toFixed(2) + '%');
      }
      time.textContent = fmt(audio.currentTime) + ' / ' + fmt(d, true);
    }

    btn.addEventListener('click', function () {
      if (audio.paused) {
        // Only one clip at a time; two teacher recordings over each other is
        // never what anyone wanted.
        var others = document.querySelectorAll('audio.saif-clip__audio');
        for (var i = 0; i < others.length; i++) {
          if (others[i] !== audio && !others[i].paused) others[i].pause();
        }
        audio.play().catch(function () { /* autoplay policy or a bad source */ });
      } else {
        audio.pause();
      }
    });

    audio.addEventListener('play', function () {
      wrap.classList.add('is-playing');
      btn.setAttribute('aria-label', 'Pause ' + title);
    });
    audio.addEventListener('pause', function () {
      wrap.classList.remove('is-playing');
      btn.setAttribute('aria-label', 'Play ' + title);
    });
    audio.addEventListener('ended', function () {
      wrap.classList.remove('is-playing');
      btn.setAttribute('aria-label', 'Play ' + title);
      audio.currentTime = 0;
      paint();
    });
    audio.addEventListener('timeupdate', paint);
    audio.addEventListener('loadedmetadata', paint);

    // If the source is unreachable, hand the reader back a native player
    // rather than a dead custom one.
    audio.addEventListener('error', function () {
      wrap.classList.remove('is-playing');
      audio.setAttribute('controls', '');
      audio.classList.remove('saif-clip__audio');
      wrap.parentNode.insertBefore(audio, wrap);
      wrap.remove();
    });

    function seekTo() {
      var d = duration();
      if (!isFinite(d) || d <= 0) return;
      audio.currentTime = (parseInt(seek.value, 10) / 1000) * d;
    }
    seek.addEventListener('input', function () {
      scrubbing = true;
      seek.style.setProperty('--pct', (parseInt(seek.value, 10) / 10).toFixed(2) + '%');
    });
    seek.addEventListener('change', function () {
      seekTo();
      scrubbing = false;
      paint();
    });

    paint();
  }

  function init() {
    var list = document.querySelectorAll('.article-template__content audio, .rte audio');
    for (var i = 0; i < list.length; i++) upgrade(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
